import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import { updateEstablishmentDirect } from '@/utils/establishmentUpdater'
import { directPatchRequest, xhrPatchRequest } from '@/utils/directFetch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { API_URL } from '@/config/constants'

interface EstablishmentDirectUpdateProps {
  establishmentId: string
}

const EstablishmentDirectUpdate: React.FC<EstablishmentDirectUpdateProps> = ({
  establishmentId,
}) => {
  const { t } = useTranslation('card')
  const [jsonData, setJsonData] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const { toast } = useToast()

  const handleUpdate = async () => {
    try {
      setIsLoading(true)

      // Parse the JSON data
      let data
      try {
        data = JSON.parse(jsonData)
      } catch (error) {
        toast({
          title: t('establishmentUpdate.invalidJsonTitle'),
          description: t('establishmentUpdate.invalidJsonDescription'),
          variant: 'destructive',
        })
        return
      }

      // Try the XHR approach first (most compatible with Postman)
      try {
        console.log(t('establishmentUpdate.attemptingXhr'))
        const url = `${API_URL}/etablissement/${establishmentId}`
        const result = await xhrPatchRequest(url, data)

        console.log(t('establishmentUpdate.xhrSuccessful'), result)

        toast({
          title: t('establishmentUpdate.successTitle'),
          description: t('establishmentUpdate.successXhrDescription'),
        })

        // Refresh the page after successful update
        setTimeout(() => {
          window.location.reload()
        }, 1500)

        return result
      } catch (xhrError) {
        // console.error(t('establishmentUpdate.xhrFailed'), xhrError)

        // Try direct fetch as a fallback
        try {
          console.log(t('establishmentUpdate.attemptingDirectFetch'))
          const url = `${API_URL}/etablissement/${establishmentId}`
          const result = await directPatchRequest(url, data)

          console.log(t('establishmentUpdate.directFetchSuccessful'), result)

          toast({
            title: t('establishmentUpdate.successTitle'),
            description: t('establishmentUpdate.successDirectFetchDescription'),
          })

          // Refresh the page after successful update
          setTimeout(() => {
            window.location.reload()
          }, 1500)

          return result
        } catch (directFetchError) {
          // console.error(
          //   t('establishmentUpdate.directFetchFailed'),
          //   directFetchError
          // )
        }
      }

      // If direct fetch fails, fall back to the original method
      console.log(t('establishmentUpdate.fallingBack'))

      // Clean the data before sending
      // Remove properties that shouldn't be sent to the API
      const propertiesToRemove = [
        'superAdminId',
        'superAdminProfileId',
        'etablissementId',
        'id',
        'createdAt',
        'updatedAt',
        'createdBy',
        'updatedBy',
        'adminProfiles',
        'teacherProfiles',
        'studentProfiles',
        'superAdminProfile',
        'cardDesign', // This property is causing the 400 error
      ]

      propertiesToRemove.forEach((prop) => {
        if (prop in data) {
          delete data[prop]
          console.log(t('establishmentUpdate.removedProperty', { property: prop }))
        }
      })

      // Call the original update function
      const result = await updateEstablishmentDirect(establishmentId, data)

      console.log(t('establishmentUpdate.fallbackResult'), result)

      toast({
        title: t('establishmentUpdate.successTitle'),
        description: t('establishmentUpdate.successFallbackDescription'),
      })

      // Refresh the page after successful update
      setTimeout(() => {
        window.location.reload()
      }, 1500)

      return result
    } catch (error) {
      // console.error(t('establishmentUpdate.errorUpdatingEstablishment'), error)

      // Provide more detailed error message
      let errorMessage = t('establishmentUpdate.unknownError')
      if (error instanceof Error) {
        errorMessage = error.message
      }

      toast({
        title: t('establishmentUpdate.errorTitle'),
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Tabs defaultValue="standard" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="standard">{t('establishmentUpdate.standardUpdate')}</TabsTrigger>
        <TabsTrigger value="api-tester">{t('establishmentUpdate.apiTester')}</TabsTrigger>
      </TabsList>

      <TabsContent value="standard">
        <Card className="w-full">
          <CardHeader>
            <CardTitle>{t('establishmentUpdate.directUpdateTitle')}</CardTitle>
            <CardDescription>
              {t('establishmentUpdate.directUpdateDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center mb-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const exampleData = `{
  "name": "aaaaaaaaaaaaaa",
  "address": "123 Education Avenue, Jerid, Tunisia",
  "logo": "./jeridoo.png",
  "url": "jerid",
  "CMSContent": "<h1>Welcome to Jerid International School</h1><p>A premier educational institution dedicated to excellence.</p>",
  "description": "Jerid International School is a leading educational institution offering comprehensive programs from kindergarten through high school. Our mission is to provide a nurturing environment where students can excel academically and develop into well-rounded individuals.",
  "heroImage": "https://example.com/hero-image.jpg",
  "galleryImages": [
    "https://example.com/gallery1.jpg",
    "https://example.com/gallery2.jpg",
    "https://example.com/gallery3.jpg"
  ],
  "contactEmail": "info@jerid-international.edu",
  "contactPhone": "+21612345678",
  "socialLinks": {
    "facebook": "https://facebook.com/jerid-international",
    "twitter": "https://twitter.com/jerid_intl",
    "instagram": "https://instagram.com/jerid_international",
    "linkedin": "https://linkedin.com/company/jerid-international"
  },
  "stats": {
    "students": 850,
    "teachers": 65,
    "courses": 42,
    "awards": 28
  },
  "services": [
    "Academic Excellence",
    "Sports Programs",
    "Arts & Music",
    "STEM Education",
    "Language Immersion"
  ],
  "isActive": true
}`
                  setJsonData(exampleData)
                }}
              >
                {t('establishmentUpdate.loadExampleData')}
              </Button>
            </div>
            <Textarea
              placeholder={t('establishmentUpdate.jsonPlaceholder')}
              value={jsonData}
              onChange={(e) => setJsonData(e.target.value)}
              className="min-h-[200px] font-mono"
            />
          </CardContent>
          <CardFooter>
            <Button
              onClick={handleUpdate}
              disabled={isLoading}
              className="w-full"
            >
              {isLoading ? t('establishmentUpdate.updatingButton') : t('establishmentUpdate.updateButton')}
            </Button>
          </CardFooter>
        </Card>
      </TabsContent>
    </Tabs>
  )
}

export default EstablishmentDirectUpdate
